import { useEffect, useRef, useState } from 'react'
import AppShell from '@/components/AppShell'
import ImageLightbox from '@/components/ImageLightbox'
import LoginPage from '@/components/LoginPage'
import { appFetch } from '@/data/api/app-fetch'
import { localDb } from '@/data/local/database'
import { restoreCloudSession, clearCloudSessionLocally } from '@/services/auth/cloud-session'
import { getActiveIdentity } from '@/services/identity/local-identity'
import { startLifecycleServices } from '@/services/lifecycle'
import { repairCloudSnapshotOnce, repairCriticalUserStatsOnce, requestSync } from '@/services/sync/sync-engine'
import { Capacitor } from '@capacitor/core'
import { FocusSoundscapeProvider } from '@/features/soundscape/FocusSoundscapeProvider'
import { migrateRemoveMistakeTag } from '@/services/migrations/remove-mistake-tag'

declare const __APP_WEB_VERSION__: string

interface UserProfile {
  id: string
  displayName: string
  statusSignature?: string
  examName?: string
  examDate?: string
  mockName?: string
  mockDate?: string
  examType?: string
  studyModules?: string[]
  focusTheme?: string
}

async function loadLocalProfile(): Promise<UserProfile> {
  const response = await appFetch('/api/auth/me')
  const data = await response.json() as { user: UserProfile }
  return data.user
}

function LoadingScreen() {
  return (
    <div className="app-loading">
      <div className="app-loading-inner">
        <span className="app-loading-logo"><img src="/app-icon-192.png" alt="闪念上岸" /></span>
        <div className="app-loading-text">闪念上岸 · 正在打开本地学习空间</div>
      </div>
    </div>
  )
}

/** Large handwriting sync and statistics repair must never be an entrance gate. */
function refreshCloudDataInBackground() {
  void (async () => {
    try { await repairCriticalUserStatsOnce() } catch { /* cached statistics remain usable */ }
    try { await requestSync() } catch { /* lifecycle retries without blocking the UI */ }
    try { await repairCloudSnapshotOnce(); await requestSync() } catch { /* pending local notes remain protected */ }
  })()
}

export default function App() {
  const [user, setUser] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const startupRestoreRef = useRef<AbortController | null>(null)
  const isAuthenticated = user !== null
  const view = new URLSearchParams(window.location.search).get('view') || 'home'

  useEffect(() => {
    let active = true
    const controller = new AbortController()
    startupRestoreRef.current = controller
    void (async () => {
      try {
        const identity = await getActiveIdentity()
        if (identity.authenticated) {
          try {
            const profile = await loadLocalProfile()
            if (active) setUser(profile)
          } catch {
            const localUser = await localDb.localUsers.get(identity.userId)
            if (active) setUser({
              id: identity.userId,
              displayName: localUser?.displayName || '学习用户',
            })
          }
        }
      } catch {
        // IndexedDB 或其他初始化失败 → 直接显示登录页
        if (active) setUser(null)
      } finally {
        // This finally only awaits local IndexedDB, never a server request.
        if (active) setLoading(false)
      }
      if (!active || !navigator.onLine) return
      try {
        await restoreCloudSession(controller.signal)
        if (!active || controller.signal.aborted) return
        const profile = await loadLocalProfile()
        if (active && !controller.signal.aborted) setUser(profile)
        refreshCloudDataInBackground()
      } catch (error) {
        if (!active || controller.signal.aborted) return
        // A slow server/offline transition must not evict a cached user.
        if ((error as { status?: number })?.status === 401) {
          setUser(null)
          void clearCloudSessionLocally().catch(() => undefined)
        }
      }
    })()
    return () => {
      active = false
      controller.abort()
    }
  }, [])

  useEffect(() => {
    const refreshProfile = () => {
      void loadLocalProfile().then(profile => setUser(current => current ? profile : null)).catch(() => undefined)
    }
    const profileUpdated = (event: Event) => {
      const detail = (event as CustomEvent<UserProfile>).detail
      if (detail) setUser(current => current ? { ...current, ...detail } : detail)
      else refreshProfile()
    }
    window.addEventListener('study-tool:synced', refreshProfile)
    window.addEventListener('study-tool:profile-updated', profileUpdated)
    return () => {
      window.removeEventListener('study-tool:synced', refreshProfile)
      window.removeEventListener('study-tool:profile-updated', profileUpdated)
    }
  }, [])

  useEffect(() => {
    if (!isAuthenticated) return
    let stop = () => {}
    let disposed = false
    void startLifecycleServices().then(cleanup => {
      if (disposed) cleanup()
      else stop = cleanup
    })
    // v2.7.5：清理其他模块冗余的「错题记录」标签及其画布标签节点（幂等并同步）
    void migrateRemoveMistakeTag()
    return () => {
      disposed = true
      stop()
    }
  }, [isAuthenticated])

  // Session expired (401) → force back to the login page
  useEffect(() => {
    const onSessionExpired = () => {
      startupRestoreRef.current?.abort()
      setUser(null)
      void clearCloudSessionLocally().catch(() => undefined)
    }
    window.addEventListener('study-tool:session-expired', onSessionExpired)
    return () => window.removeEventListener('study-tool:session-expired', onSessionExpired)
  }, [])

  // Auto-update check for Android
  useEffect(() => {
    if (!isAuthenticated || !Capacitor.isNativePlatform()) return
    void (async () => {
      try {
        const resp = await fetch('https://shangancard.me/app-updates/stable/manifest.json', { cache: 'no-store' })
        if (!resp.ok) return
        const manifest = await resp.json()
        const payload = JSON.parse(manifest.payload)
        const current = typeof __APP_WEB_VERSION__ !== 'undefined' ? __APP_WEB_VERSION__ : '1.0.0'
        if (payload.version !== current && confirm(`发现新版本 v${payload.version}，是否立即更新？`)) {
          window.open('https://shangancard.me/download/ShanganCard.apk', '_blank')
        }
      } catch { /* silent */ }
    })()
  }, [isAuthenticated])

  if (loading) return <LoadingScreen />
  if (!user) {
    return <LoginPage onLogin={async () => {
      startupRestoreRef.current?.abort()
      setUser(await loadLocalProfile())
      refreshCloudDataInBackground()
    }} />
  }
  return (
    <>
      <FocusSoundscapeProvider>
        <AppShell user={user} view={view} />
      </FocusSoundscapeProvider>
      <ImageLightbox />
    </>
  )
}
